import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Endeavors API service
 */
export const EndeavorApi = {
  /**
   * Get all endeavors for a user (paginated)
   * @param {string|number} userId - User ID
   * @param {Object} options - Optional parameters
   * @param {number} [options.page=0] - Page number (0-indexed)
   * @param {number} [options.size=1000] - Page size
   * @returns {Promise<Response>} Paginated user endeavors response with content array
   */
  getEndeavors: async (userId, options = {}) => {
    const { page = 0, size = 1000 } = options;
    const queryParams = new URLSearchParams({ 
      page: String(page), 
      size: String(size) 
    }).toString();
    
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${userId}/endeavors?${queryParams}`, {
      method: 'GET',
    });
  },

  /**
   * Get endeavor by ID
   * @param {string|number} id - Endeavor ID
   * @returns {Promise<Response>} Endeavor response
   */
  getEndeavor: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${id}`, {
      method: 'GET',
    });
  },

  /**
   * Create endeavor
   * @param {Object} data - Endeavor data
   * @returns {Promise<Response>} Create response
   */
  createEndeavor: async (data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Bulk-create all events and assignments for a course in one request.
   * Assignments can reference events via `tempEventIndex` (0-based index into
   * the events array) so the backend resolves the real eventId after persistence.
   * @param {string|number} endeavorId - The course/endeavor ID
   * @param {{ events: Array, assignments: Array }} payload - Bulk content payload
   * @returns {Promise<Response>} Response with { eventsCreated, assignmentsCreated, events, assignments }
   */
  bulkCreateCourseContent: async (endeavorId, payload) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${endeavorId}/content/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  /**
   * Update endeavor
   * @param {string|number} id - Endeavor ID
   * @param {Object} data - Updated endeavor data
   * @returns {Promise<Response>} Update response
   */
  updateEndeavor: async (id, data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Patch endeavor (safe GET+merge+PUT fallback)
   * Fetches current resource, shallow-merges the patch, and PUTs full resource.
   * This preserves patch semantics for callers while supporting PUT-only backends.
   */
  patchEndeavor: async (id, patch) => {
    // Fetch current resource
    const currentResp = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${id}`, {
      method: 'GET',
    });

    let current = {};
    if (currentResp && currentResp.ok) {
      try {
        current = await currentResp.json();
      } catch (_) {
        current = {};
      }
    }

    // Shallow merge patch into current
    const merged = { ...(current || {}), ...(patch || {}) };

    // Debug: log merged payload so callers can inspect what's being PUT
    try { console.log('[EndeavorApi] patchEndeavor merged payload:', merged); } catch (e) { /* ignore */ }

    // Persist full resource via existing PUT endpoint
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(merged),
    });
  },

  /**
   * Delete endeavor
   * @param {string|number} id - Endeavor ID
   * @returns {Promise<Response>} Delete response
   */
  deleteEndeavor: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Promote an existing personal course to a student-shared course so the
   * owner can issue invite codes. Backend enforces owner-only.
   * @param {string|number} id - Endeavor ID
   * @returns {Promise<Response>} Updated endeavor response
   */
  shareEndeavor: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${id}/share`, {
      method: 'POST',
    });
  },

  /**
   * Get endeavors by user
   * @param {string|number} userId - User ID
   * @param {Object} options - Optional parameters (page, size)
   * @returns {Promise<Response>} User endeavors response
   */
  getEndeavorsByUser: async (userId, options = {}) => {
    const { page = 0, size = 10 } = options;
    const queryParams = new URLSearchParams({ page, size }).toString();
    
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${userId}/endeavors?${queryParams}`, {
      method: 'GET',
    });
  },

  /**
   * Get endeavor metadata
   * @param {string|number} endeavorId - Endeavor ID
   * @returns {Promise<Response>} Endeavor metadata response
   */
  getEndeavorMetadata: async (endeavorId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${endeavorId}/metadata`, {
      method: 'GET',
    });
  },

  /**
   * Get endeavor assignments
   * @param {string|number} endeavorId - Endeavor ID
   * @returns {Promise<Response>} Endeavor assignments response
   */
  getEndeavorAssignments: async (endeavorId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${endeavorId}/assignments`, {
      method: 'GET',
    });
  },

  /**
   * Get endeavor events
   * @param {string|number} endeavorId - Endeavor ID
   * @returns {Promise<Response>} Endeavor events response
   */
  getEndeavorEvents: async (endeavorId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${endeavorId}/events`, {
      method: 'GET',
    });
  },

  /**
   * Get fully hydrated endeavors for user (includes events, assignments, syllabus documents)
   * @param {string|number} userId - User ID
   * @returns {Promise<Response>} Fully hydrated endeavors response
   */
  getEndeavorsByUserFull: async (userId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${userId}/endeavors/full`, {
      method: 'GET',
    });
  },

  /**
   * Create a shared endeavor (sets isShared=true, auto-enrolls creator as owner)
   * @param {Object} data - Endeavor data
   * @returns {Promise<Response>} Created endeavor response
   */
  createSharedEndeavor: async (data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/shared`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Get all endeavors for the current user (owned personal + enrolled shared)
   * @returns {Promise<Response>} Combined endeavor list
   */
  getAllEndeavorsForUser: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/endeavors/all`, {
      method: 'GET',
    });
  },

  /**
   * Search shared courses by metadata (course code, name, instructor)
   * @param {Object} params - Search parameters
   * @param {string} [params.courseCode]
   * @param {string} [params.courseName]
   * @param {string} [params.instructor]
   * @returns {Promise<Response>} Array of matching shared EndeavorDTOs
   */
  searchSharedCourses: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.courseCode) queryParams.set('courseCode', params.courseCode);
    if (params.courseName) queryParams.set('courseName', params.courseName);
    if (params.instructor) queryParams.set('instructor', params.instructor);
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/shared/search?${queryParams.toString()}`, {
      method: 'GET',
    });
  },
};